import { FC, useEffect, useState } from "react";
import { SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@capacit-com/lara-law-shared-ui";
import {  } from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";
import { SquarePenIcon, TrashIcon } from "lucide-react";
import { useConversationHub } from "@/hooks/useConversationHub";
import { useDocumentReferenceStore } from "@/stores/document-reference.store";
import Conversation from "@/models/conversation/Conversation";
import { groupConversationsByDate } from "@/pages/chat/utils/GroupConversations";
import ConversationListSkeleton from "@/pages/chat/components/ConversationListSkeleton";
import SmallPagination from "../pagination/SmallPagination";

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@capacit-com/lara-law-shared-ui";
import { useNewConversationFiltersStore } from "@/stores/new-conversation-filters.store";

const ConversationSidebar: FC = () => {
  const {
    conversations,
    selectConversation,
    loading,
    selectedConversation,
    deleteConversation,
    isLoadingMessages,
  } = useConversationHub();
  const { reset } = useDocumentReferenceStore();
  const { resetFilters, resetWorkspaceContext } =
    useNewConversationFiltersStore();

  const handleCreateConversation = () => {
    resetFilters();
    resetWorkspaceContext();
    selectConversation(undefined);
  };

  const [conversationsGroupedByDate, setConversationsGroupedByDate] = useState<{
    [key: string]: Conversation[];
  }>();

  const [isCreatePopoverOpen, setIsCreatePopoverOpen] = useState(false);

  useEffect(() => {
    setConversationsGroupedByDate(groupConversationsByDate(conversations));
  }, [conversations]);

  return (
    <>
      <SidebarHeader className="h-12 border-b">
        <div className="flex justify-between items-center h-full">
          <Popover
            open={isCreatePopoverOpen}
            onOpenChange={setIsCreatePopoverOpen}
          >
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn("size-7")}
                disabled={!selectedConversation}
              >
                <SquarePenIcon />
                <span className="sr-only">Opret Samtale</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-40">
              <div className="flex flex-col">
                <Button
                  variant="ghost"
                  className="justify-start"
                  onClick={() => {
                    handleCreateConversation();
                    setIsCreatePopoverOpen(false);
                  }}
                >
                  Ny samtale
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start"
                  onClick={() => {
                    selectConversation(undefined);
                    setIsCreatePopoverOpen(false);
                  }}
                >
                  Bevar filtre
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          <p className="font-semibold">Samtaler</p>
          <div className="size-7" />
        </div>
      </SidebarHeader>
      <SidebarContent className="chat-conversation-sidebar">
        {loading ? (
          <div className="relative mt-2 flex-1 px-4 overflow-y-auto h-full flex gap-6 flex-col last:pb-6 mr-0.5">
            <ConversationListSkeleton />
          </div>
        ) : (
          <>
            {conversationsGroupedByDate &&
            Object.keys(conversationsGroupedByDate).length === 0 ? (
              <div className="flex justify-center items-center h-full">
                <p>Ingen samtaler</p>
              </div>
            ) : (
              Object.keys(conversationsGroupedByDate || {}).map((key) => (
                <SidebarGroup key={key + "-group"}>
                  <SidebarGroupLabel>{key}</SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {conversationsGroupedByDate?.[key]?.map(
                        (conversation) =>
                          conversation.id !== "temp_session" && (
                            <SidebarMenuItem
                              key={conversation.id}
                              id={`conversation-${conversation.id}`}
                            >
                              <div className="group/item">
                                <SidebarMenuButton
                                  onClick={() => {
                                    selectConversation(conversation);
                                    reset();
                                  }}
                                  className={cn(
                                    "cursor-pointer justify-between",
                                    {
                                      "bg-sidebar-accent text-sidebar-accent-foreground":
                                        (isLoadingMessages.isLoading &&
                                          isLoadingMessages.conversationId ===
                                            conversation.id) ||
                                        (selectedConversation?.id ===
                                          conversation.id &&
                                          !isLoadingMessages.isLoading),
                                    }
                                  )}
                                >
                                  <p className="text-xs truncate">
                                    {conversation.title
                                      ? conversation.title
                                      : "Unavngivet Chat"}
                                  </p>
                                  <TrashIcon
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteConversation(conversation);
                                    }}
                                    className={cn(
                                      "size-4 shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity hover:text-destructive",
                                      selectedConversation?.id ===
                                        conversation.id &&
                                        "text-sidebar-accent-foreground"
                                    )}
                                  />
                                </SidebarMenuButton>
                              </div>
                            </SidebarMenuItem>
                          )
                      )}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              ))
            )}
          </>
        )}
      </SidebarContent>
      <SmallPagination />
    </>
  );
};

export default ConversationSidebar;
